import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { percentageOneDecimal } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "./dataAtoms/shared";
import { COLUMN_HEADING } from "./tableStyles";

/**
 * What is estimated: the texts of the two kinds have the same keys under the
 * name of the kind.
 */
export type EstimateKind = "weightedValue" | "weightedAwards";

/** An estimate, its weight among the estimates, and the button that removes it. */
const COLUMNS = "minmax(0, 1fr) 132px 34px";

/**
 * Several estimates of one figure, each with a weight, as rows in a frame: the
 * headings of the columns over them, and under them the button that adds an
 * estimate beside the figure the estimates result in.
 */
export const WeightedEstimates = ({
  kind,
  showWeights,
  result,
  onAdd,
  children,
}: {
  kind: EstimateKind;
  // a single estimate has no weight to show
  showWeights: boolean;
  // the weighted figure, formatted; left out while there is none to state
  result?: string;
  onAdd: () => void;
  // one WeightedEstimate per estimate
  children: ReactNode;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: kind });

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2.25,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: COLUMNS,
          gap: 1.5,
          px: 1.5,
          py: 1,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={COLUMN_HEADING}>{t("label.value")}</Box>
        {showWeights && <Box sx={COLUMN_HEADING}>{t("label.weight")}</Box>}
      </Box>
      {children}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAdd}
          title={t("tip.button.add")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {t("label.button.add")}
        </Button>
        {result !== undefined && (
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary" }}
            >
              {t("label.result")}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.3, ...TABULAR_NUMERALS }}
            >
              {result}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * One estimate among several: the control that sets it, its weight with the
 * share of all weights that it comes to, and the button that removes it.
 */
export const WeightedEstimate = ({
  kind,
  weight,
  share,
  onWeightChange,
  onDelete,
  children,
}: {
  kind: EstimateKind;
  // left out, the estimate shows no weight
  weight?: number;
  share?: number;
  onWeightChange?: (newWeight: number) => void;
  // left out, the estimate cannot be removed
  onDelete?: () => void;
  // the control that sets the estimate
  children: ReactNode;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: kind });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: COLUMNS,
        gap: 1.5,
        alignItems: "center",
        px: 1.5,
        py: 1.375,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      {weight !== undefined ? (
        <TextField
          size="small"
          type="number"
          value={weight}
          onChange={(e) => {
            const newWeight = Number.parseFloat(e.target.value);
            if (newWeight > 0) onWeightChange?.(newWeight);
          }}
          title={t("tip.weight")}
          slotProps={{
            htmlInput: { "aria-label": t("label.weight") },
            input:
              share !== undefined
                ? {
                    endAdornment: (
                      <InputAdornment position="end" title={t("tip.share")}>
                        {percentageOneDecimal.format(share)}
                      </InputAdornment>
                    ),
                  }
                : {},
          }}
        />
      ) : (
        <span />
      )}
      {onDelete !== undefined && (
        <IconButton
          size="small"
          onClick={onDelete}
          aria-label={t("tip.button.delete")}
          title={t("tip.button.delete")}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
